import { FormError } from 'components/FormError/FormError';
import { Input } from 'components/Input/Input';
import { Label } from 'components/Label/Label';
import { Select } from 'components/Select/Select';
import { COUNTRIES, SALUTATIONS } from 'lib/options';
import { useEffect } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

export const MemberContact = () => {
    const {
        register,
        control,
        unregister,
        formState: { errors },
    } = useFormContext();
    const { t } = useTranslation('forms');

    // Watch the monthly consumption overview checkbox
    const monthlyOverview = useWatch({ control, name: 'monthlyConsumptionOverview' });

    useEffect(() => {
        return () => unregister();
    }, [unregister]);

    const translatedSalutations = SALUTATIONS.map(item => ({ ...item, label: t(item.value), originalLabel: item.label }));

    return (
        <div className="flex flex-col gap-6">
            <div className="flex gap-6">
                <div className="flex-1">
                    <Label>Salutation</Label>
                    <Controller
                        control={control}
                        name="salutation"
                        render={({ field: { onChange, value } }) => (
                            <Select
                                options={translatedSalutations}
                                onChange={(val: any) => onChange(val.value)}
                                value={translatedSalutations.find(s => s.value === value)}
                                filterOption={(option, inputValue) => {
                                    const searchValue = inputValue.toLowerCase();
                                    const translatedLabel = option.label?.toLowerCase() || '';
                                    const originalLabel = (option as any).originalLabel?.toLowerCase() || '';
                                    const valueLabel = option.value?.toLowerCase() || '';
                                    return translatedLabel.includes(searchValue) || 
                                           originalLabel.includes(searchValue) || 
                                           valueLabel.includes(searchValue);
                                }}
                            />
                        )}
                    />
                </div>
                <div className="flex-1" />
            </div>

            <div className="flex gap-6">
                <div className="flex-1">
                    <Label>{t('firstName')}</Label>
                    <Input
                        {...register('name', {
                            required: t('firstNameRequired'),
                        })}
                        placeholder={t('firstName')}
                        error={!!errors.name}
                    />
                    <FormError error={errors.name} />
                </div>
                <div className="flex-1">
                    <Label> {t('lastName')}</Label>
                    <Input
                        {...register('lastName', {
                            required: t('lastNameRequired'),
                        })}
                        placeholder={t('lastName')}
                        error={!!errors.lastName}
                    />

                    <FormError error={errors.lastName} />
                </div>
            </div>

            <div className="flex gap-6">
                <div className="flex-1">
                    <Label>{t('personalPhone')}</Label>
                    <Input {...register('phoneNumber')} placeholder={t('personalPhone')} error={!!errors.phoneNumber} />
                    <FormError error={errors.phoneNumber} />
                </div>
                <div className="flex-1">
                    <Label>{t('businessPhone')}</Label>
                    <Input
                        {...register('secondaryPhoneNumber')}
                        placeholder={t('businessPhone')}
                        defaultValue={undefined}
                    />
                </div>
            </div>

            <div className="flex gap-6">
                <div className="flex-1">
                    <Label>{t('preview.email')}</Label>
                    <Input 
                        {...register('email', {
                            validate: (value) => {
                                if (monthlyOverview && (!value || value.trim() === '')) {
                                    return t('emailRequired');
                                }
                                return true;
                            }
                        })} 
                        placeholder={t('preview.email')} 
                        error={!!errors.email} 
                    />
                    <FormError error={errors.email} />
                </div>
                <div className="flex-1">
                    <Label>{t('secondaryEmail')}</Label>
                    <Input {...register('secondaryEmail')} placeholder={t('secondaryEmail')} defaultValue={undefined} />
                </div>
            </div>

            <div className="flex gap-6">
                <div className="flex-1">
                    <Label>{t('address')}</Label>
                    <Input {...register('address')} placeholder={t('address')} />
                </div>
                <div className="flex gap-6 flex-1">
                    <div className="flex-1">
                        <Label>{t('zipCode')}</Label>
                        <Input {...register('zipCode')} placeholder={t('zipCode')} />
                    </div>
                    <div className="flex-1">
                        <Label>{t('poBox')}</Label>
                        <Input {...register('poBox')} placeholder={t('poBox')} />
                    </div>
                </div>
            </div>

            <div className="flex gap-6">
                <div className="flex-1">
                    <Label> {t('city')}</Label>
                    <Input {...register('city')} placeholder={t('city')} />
                </div>
                <div className="flex-1">
                    <Label>{t('country')}</Label>
                    <Controller
                        control={control}
                        name="country"
                        render={({ field: { onChange, value } }) => (
                            <Select
                                options={COUNTRIES}
                                onChange={(val: any) => onChange(val.value)}
                                menuPortalTarget={document.body}
                                styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                                value={COUNTRIES.find(c => c.value === value)}
                            />
                        )}
                    />
                </div>
            </div>
        </div>
    );
};
